import { useState, useEffect, useCallback, useRef } from 'react';
import { getSupabaseClient, isSupabaseConnected } from '@/lib/supabase';
import {
  posts as mockPosts,
  categories as mockCategories,
  type Post,
  type Category,
} from '@/mocks/blog-data';

/* ─── Global Cache with TTL ─── */
interface CacheEntry<T> {
  data: T;
  ts: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const CACHE_TTL = 60_000; // 60 seconds

function getCache<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return entry.data as T;
}

function setCache<T>(key: string, data: T) {
  cache.set(key, { data, ts: Date.now() });
}

function invalidateCache(prefix: string) {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

/* ─── increment views on a post ─── */

export async function incrementPostViews(id: string) {
  if (!isSupabaseConnected()) return;
  try {
    const sb = getSupabaseClient();
    if (!sb) return;
    const { data } = await sb
      .from('posts')
      .select('views')
      .eq('id', id)
      .maybeSingle();
    const currentViews = (data?.views || 0) as number;
    await sb.from('posts').update({ views: currentViews + 1 }).eq('id', id);
  } catch {
    // Silently fail for view counting
  }
}

export async function getTotalViews() {
  if (!isSupabaseConnected()) {
    return mockPosts.reduce((sum, p) => sum + (p.views || 0), 0);
  }
  const sb = getSupabaseClient();
  if (!sb) return 0;
  const { data, error } = await sb.from('posts').select('views');
  if (error || !data) return 0;
  return data.reduce((sum: number, row: { views?: number }) => sum + (row.views || 0), 0);
}

/* ─── Top 10 most-viewed posts (for dashboard) ─── */

export function useTopPosts(limit: number = 10) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = getCache<Post[]>(`topPosts-${limit}`);
    if (cached) {
      setPosts(cached);
      setLoading(false);
      return;
    }

    if (!isSupabaseConnected()) {
      const sorted = [...mockPosts]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, limit);
      setPosts(sorted);
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      const sb = getSupabaseClient();
      if (!sb) {
        setLoading(false);
        return;
      }
      const { data, error } = await sb
        .from('posts')
        .select('*')
        .eq('status', 'published')
        .order('views', { ascending: false })
        .limit(limit);

      if (!error && data) {
        const mapped = data.map(mapPostRow);
        setPosts(mapped);
        setCache(`topPosts-${limit}`, mapped);
      }
      setLoading(false);
    };

    fetch();
  }, [limit]);

  return { posts, loading };
}

/* ─── Posts ─── */

export function usePublishedPosts() {
  const [posts, setPosts] = useState<Post[]>(getCache<Post[]>('publishedPosts') ?? mockPosts);
  const [loading, setLoading] = useState(!getCache('publishedPosts'));

  useEffect(() => {
    const cached = getCache<Post[]>('publishedPosts');
    if (cached) {
      setPosts(cached);
      setLoading(false);
      return;
    }

    if (!isSupabaseConnected()) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      const sb = getSupabaseClient();
      if (!sb) {
        setLoading(false);
        return;
      }
      const { data, error } = await sb
        .from('posts')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (!error && data) {
        const mapped = data.map(mapPostRow);
        setPosts(mapped);
        setCache('publishedPosts', mapped);
      }
      setLoading(false);
    };

    fetch();
  }, []);

  return { posts, loading };
}

export function usePostBySlug(slug: string | undefined) {
  const [post, setPost] = useState<Post | null>(getCache<Post>(`post-slug-${slug}`) ?? null);
  const [loading, setLoading] = useState(!getCache(`post-slug-${slug}`));

  useEffect(() => {
    if (!slug) return;

    const cached = getCache<Post>(`post-slug-${slug}`);
    if (cached) {
      setPost(cached);
      setLoading(false);
      return;
    }

    const mock = mockPosts.find((p) => p.slug === slug);
    if (mock) {
      setPost(mock);
      setCache(`post-slug-${slug}`, mock);
    }

    if (!isSupabaseConnected()) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      const sb = getSupabaseClient();
      if (!sb) {
        setLoading(false);
        return;
      }
      const { data, error } = await sb
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();

      if (!error && data) {
        const mapped = mapPostRow(data);
        setPost(mapped);
        setCache(`post-slug-${slug}`, mapped);
      }
      setLoading(false);
    };

    fetch();
  }, [slug]);

  return { post, loading };
}

export function usePostsByCategory(categorySlug: string | undefined) {
  const [posts, setPosts] = useState<Post[]>(getCache<Post[]>(`posts-cat-${categorySlug}`) ?? []);
  const [loading, setLoading] = useState(!getCache(`posts-cat-${categorySlug}`));

  const cat = mockCategories.find((c) => c.slug === categorySlug);
  const mockFiltered = cat
    ? mockPosts.filter((p) => p.categoryId === cat.id && p.status === 'published')
    : [];

  useEffect(() => {
    const cached = getCache<Post[]>(`posts-cat-${categorySlug}`);
    if (cached) {
      setPosts(cached);
      setLoading(false);
      return;
    }

    setPosts(mockFiltered);
    if (!isSupabaseConnected() || !categorySlug) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      const sb = getSupabaseClient();
      if (!sb) {
        setLoading(false);
        return;
      }

      const { data: catData } = await sb
        .from('categories')
        .select('id')
        .eq('slug', categorySlug)
        .maybeSingle();

      if (!catData) {
        setLoading(false);
        return;
      }

      const { data, error } = await sb
        .from('posts')
        .select('*')
        .eq('category_id', catData.id)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (!error && data) {
        const mapped = data.map(mapPostRow);
        setPosts(mapped);
        setCache(`posts-cat-${categorySlug}`, mapped);
      }
      setLoading(false);
    };

    fetch();
  }, [categorySlug]);

  return { posts, loading };
}

/* ─── Mixed Related Posts (Leia Mais) ─── */

export function useMixedRelatedPosts(
  currentPostId: string | undefined,
  currentCategoryId: string | undefined
) {
  const { posts: allPosts } = usePublishedPosts();
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);

  useEffect(() => {
    if (!currentPostId || !currentCategoryId || allPosts.length === 0) {
      setRelatedPosts([]);
      return;
    }

    const eligible = allPosts.filter(
      (p) => p.id !== currentPostId && p.status === 'published'
    );

    const sameCat = eligible
      .filter((p) => p.categoryId === currentCategoryId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 2);

    const otherCat = eligible
      .filter((p) => p.categoryId !== currentCategoryId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const selectedOthers: Post[] = [];
    const usedCatIds = new Set<string>();

    for (const post of otherCat) {
      if (selectedOthers.length >= 6 - sameCat.length) break;
      if (!usedCatIds.has(post.categoryId)) {
        selectedOthers.push(post);
        usedCatIds.add(post.categoryId);
      }
    }

    for (const post of otherCat) {
      if (selectedOthers.length >= 6 - sameCat.length) break;
      if (!selectedOthers.find((p) => p.id === post.id)) {
        selectedOthers.push(post);
      }
    }

    setRelatedPosts([...sameCat, ...selectedOthers].slice(0, 6));
  }, [currentPostId, currentCategoryId, allPosts]);

  return relatedPosts;
}

/* ─── Categories ─── */

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>(
    getCache<Category[]>('categories') ?? mockCategories
  );
  const [loading, setLoading] = useState(!getCache('categories'));

  useEffect(() => {
    const cached = getCache<Category[]>('categories');
    if (cached) {
      setCategories(cached);
      setLoading(false);
      return;
    }

    if (!isSupabaseConnected()) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      const sb = getSupabaseClient();
      if (!sb) return;
      const { data, error } = await sb
        .from('categories')
        .select('*')
        .order('name');

      if (!error && data) {
        const mapped = data.map((d) => ({ id: d.id, name: d.name, slug: d.slug }));
        setCategories(mapped);
        setCache('categories', mapped);
      }
    };

    fetch();
  }, []);

  return categories;
}

export function useCategoryBySlug(slug: string | undefined) {
  const [category, setCategory] = useState<Category | undefined>(
    getCache<Category>(`category-${slug}`) ?? mockCategories.find((c) => c.slug === slug)
  );
  const [loading, setLoading] = useState(!getCache(`category-${slug}`));

  useEffect(() => {
    if (!slug) return;

    const cached = getCache<Category>(`category-${slug}`);
    if (cached) {
      setCategory(cached);
      setLoading(false);
      return;
    }

    if (!isSupabaseConnected()) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      const sb = getSupabaseClient();
      if (!sb) return;
      const { data, error } = await sb
        .from('categories')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (!error && data) {
        const mapped = { id: data.id, name: data.name, slug: data.slug };
        setCategory(mapped);
        setCache(`category-${slug}`, mapped);
      }
      setLoading(false);
    };

    fetch();
  }, [slug]);

  return category;
}

/** Fast category name lookup — uses cache first, no extra hook re-renders */
export function getCategoryNameById(id: string): string {
  const cached = getCache<Category[]>('categories');
  if (cached) {
    const cat = cached.find((c) => c.id === id);
    if (cat) return cat.name;
  }
  const cat = mockCategories.find((c) => c.id === id);
  return cat ? cat.name : 'Geral';
}

/** Backward-compatible hook wrapper for component usage */
export function useCategoryNameById(id: string): string {
  return getCategoryNameById(id);
}

/* ─── Post by ID (for admin edit) ─── */

export function usePostById(id: string | undefined) {
  const [post, setPost] = useState<Post | null>(getCache<Post>(`post-id-${id}`) ?? null);
  const [loading, setLoading] = useState(!getCache(`post-id-${id}`));

  useEffect(() => {
    if (!id) return;

    const cached = getCache<Post>(`post-id-${id}`);
    if (cached) {
      setPost(cached);
      setLoading(false);
      return;
    }

    const mock = mockPosts.find((p) => p.id === id);
    if (mock) {
      setPost(mock);
      setCache(`post-id-${id}`, mock);
    }

    if (!isSupabaseConnected()) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      const sb = getSupabaseClient();
      if (!sb) {
        setLoading(false);
        return;
      }
      const { data, error } = await sb
        .from('posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (!error && data) {
        const mapped = mapPostRow(data);
        setPost(mapped);
        setCache(`post-id-${id}`, mapped);
      }
      setLoading(false);
    };

    fetch();
  }, [id]);

  return { post, loading };
}

/* ─── Admin: all posts (published + draft) ─── */

export function useAllPosts() {
  const [posts, setPosts] = useState<Post[]>(getCache<Post[]>('allPosts') ?? mockPosts);
  const [loading, setLoading] = useState(!getCache('allPosts'));

  const refresh = useCallback(async () => {
    if (!isSupabaseConnected()) return;

    setLoading(true);
    const sb = getSupabaseClient();
    if (!sb) {
      setLoading(false);
      return;
    }
    const { data, error } = await sb
      .from('posts')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      const mapped = data.map(mapPostRow);
      setPosts(mapped);
      setCache('allPosts', mapped);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const cached = getCache<Post[]>('allPosts');
    if (cached) {
      setPosts(cached);
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return { posts, loading, refresh };
}

/* ─── Admin: CRUD helpers ─── */

export async function createPost(post: Omit<Post, 'id' | 'createdAt' | 'updatedAt'>) {
  if (!isSupabaseConnected()) return { error: 'Supabase não conectado' };

  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };

  const { data, error } = await sb.from('posts').insert({
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt,
    content: post.content,
    category_id: post.categoryId,
    image: post.image,
    image_alt: post.imageAlt,
    meta_title: post.metaTitle,
    meta_description: post.metaDescription,
    keywords: post.keywords,
    main_keyword: post.mainKeyword,
    status: post.status,
    read_time: post.readTime,
    auto_link_enabled: post.autoLinkEnabled,
    auto_link_limit: post.autoLinkLimit,
    author_name: post.authorName,
    whatsapp_cta: post.whatsappCTA,
  }).select().single();

  invalidateCache('publishedPosts');
  invalidateCache('allPosts');
  invalidateCache('topPosts');

  return { data, error: error?.message };
}

export async function updatePost(id: string, post: Partial<Post>) {
  if (!isSupabaseConnected()) return { error: 'Supabase não conectado' };

  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };

  const payload: Record<string, unknown> = {};
  if (post.title !== undefined) payload.title = post.title;
  if (post.slug !== undefined) payload.slug = post.slug;
  if (post.excerpt !== undefined) payload.excerpt = post.excerpt;
  if (post.content !== undefined) payload.content = post.content;
  if (post.categoryId !== undefined) payload.category_id = post.categoryId;
  if (post.image !== undefined) payload.image = post.image;
  if (post.imageAlt !== undefined) payload.image_alt = post.imageAlt;
  if (post.metaTitle !== undefined) payload.meta_title = post.metaTitle;
  if (post.metaDescription !== undefined) payload.meta_description = post.metaDescription;
  if (post.keywords !== undefined) payload.keywords = post.keywords;
  if (post.mainKeyword !== undefined) payload.main_keyword = post.mainKeyword;
  if (post.status !== undefined) payload.status = post.status;
  if (post.readTime !== undefined) payload.read_time = post.readTime;
  if (post.views !== undefined) payload.views = post.views;
  if (post.autoLinkEnabled !== undefined) payload.auto_link_enabled = post.autoLinkEnabled;
  if (post.autoLinkLimit !== undefined) payload.auto_link_limit = post.autoLinkLimit;
  if (post.authorName !== undefined) payload.author_name = post.authorName;
  if (post.whatsappCTA !== undefined) payload.whatsapp_cta = post.whatsappCTA;

  const { data, error } = await sb.from('posts').update(payload).eq('id', id).select().single();

  invalidateCache('publishedPosts');
  invalidateCache('allPosts');
  invalidateCache('topPosts');
  invalidateCache(`post-id-${id}`);
  invalidateCache(`post-slug-${post.slug}`);

  return { data, error: error?.message };
}

export async function deletePost(id: string) {
  if (!isSupabaseConnected()) return { error: 'Supabase não conectado' };

  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };

  const { error } = await sb.from('posts').delete().eq('id', id);

  invalidateCache('publishedPosts');
  invalidateCache('allPosts');
  invalidateCache('topPosts');

  return { error: error?.message };
}

/* ─── Categories CRUD ─── */

export async function createCategory(name: string, slug: string) {
  if (!isSupabaseConnected()) return { error: 'Supabase não conectado' };

  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };

  const { data, error } = await sb
    .from('categories')
    .insert({ name, slug })
    .select()
    .single();

  invalidateCache('categories');

  return { data, error: error?.message };
}

export async function updateCategory(id: string, name: string, slug: string) {
  if (!isSupabaseConnected()) return { error: 'Supabase não conectado' };

  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };

  const { data, error } = await sb
    .from('categories')
    .update({ name, slug })
    .eq('id', id)
    .select()
    .single();

  invalidateCache('categories');

  return { data, error: error?.message };
}

export async function deleteCategory(id: string) {
  if (!isSupabaseConnected()) return { error: 'Supabase não conectado' };

  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };

  const { error } = await sb.from('categories').delete().eq('id', id);

  invalidateCache('categories');

  return { error: error?.message };
}

/* ─── Helpers ─── */

function mapPostRow(d: Record<string, unknown>): Post {
  return {
    id: d.id as string,
    title: d.title as string,
    slug: d.slug as string,
    excerpt: (d.excerpt as string) || '',
    content: d.content as string,
    categoryId: d.category_id as string,
    image: d.image as string,
    imageAlt: (d.image_alt as string) || '',
    metaTitle: d.meta_title as string,
    metaDescription: d.meta_description as string,
    keywords: d.keywords as string,
    mainKeyword: (d.main_keyword as string) || '',
    status: d.status as 'published' | 'draft',
    readTime: (d.read_time as number) || 5,
    views: (d.views as number) || 0,
    autoLinkEnabled: (d.auto_link_enabled as boolean) ?? true,
    autoLinkLimit: (d.auto_link_limit as number) ?? 2,
    authorName: (d.author_name as string) || 'Equipe Eletrônica 2000',
    whatsappCTA: (d.whatsapp_cta as boolean) ?? false,
    createdAt: d.created_at as string,
    updatedAt: d.updated_at as string,
  };
}